import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import { useToast } from "../../components/Toast/toastHook";
import { GET_ALL_SERVICES } from "../../graphql/agentQueries";
import { Table, Button } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { formatDate } from "../../utils/dateFormat";

const AgentServices = () => {
  const [services, setServices] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [totalCount, setTotalCount] = useState(0);
  const { companyId } = useParams();
  const navigate = useNavigate();
  const toast = useToast(4000);

  const { loading, error, data, refetch } = useQuery(GET_ALL_SERVICES, {
    variables: {
      companyId,
      pagination,
      searchText: searchText || undefined,
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (data?.getAllServices) {
      setServices(data.getAllServices.services || []);
      setTotalCount(data.getAllServices.totalCount || 0);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      toast("error", error.message);
    }
  }, [error]);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      page: paginationConfig.current,
      limit: paginationConfig.pageSize,
    });
  };

  const handleViewSubCompanies = (service) => {
    navigate(`/company/${companyId}/agent/services/${service._id}/sub-companies`, {
      state: { serviceName: service.title },
    });
  };

  const columns = [
    {
      title: "Icon",
      dataIndex: "icon",
      key: "icon",
      width: 80,
      render: (icon) =>
        icon ? (
          <img
            src={icon}
            alt="Service Icon"
            className="w-10 h-10 object-contain"
          />
        ) : (
          <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
            <span className="text-gray-400 text-xs">N/A</span>
          </div>
        ),
    },
    {
      title: "Service Name",
      dataIndex: "title",
      key: "title",
      render: (title) => <span className="font-medium text-black">{title}</span>,
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date) || "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => handleViewSubCompanies(record)}
          title="View Sub Companies"
        >
          View
        </Button>
      ),
    },
  ];

  const firstLoad = loading && services.length === 0;

  return (
    <div id="agent-services">
      <Breadcrumb pageName="Services" />
      <div className="pb-6">
        <Card>
          <div className="m-4">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <span className="font-bold text-lg">
                  Services ({totalCount})
                </span>
                <div className="relative">
                  <div className="absolute top-1/2 left-1 -translate-y-1/2">
                    <svg
                      className="fill-gray-400"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9.16666 3.33332C5.945 3.33332 3.33332 5.945 3.33332 9.16666C3.33332 12.3883 5.945 15 9.16666 15C12.3883 15 15 12.3883 15 9.16666C15 5.945 12.3883 3.33332 9.16666 3.33332ZM1.66666 9.16666C1.66666 5.02452 5.02452 1.66666 9.16666 1.66666C13.3088 1.66666 16.6667 5.02452 16.6667 9.16666C16.6667 13.3088 13.3088 16.6667 9.16666 16.6667C5.02452 16.6667 1.66666 13.3088 1.66666 9.16666Z"
                        fill=""
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.2857 13.2857C13.6112 12.9603 14.1388 12.9603 14.4642 13.2857L18.0892 16.9107C18.4147 17.2362 18.4147 17.7638 18.0892 18.0892C17.7638 18.4147 17.2362 18.4147 16.9107 18.0892L13.2857 14.4642C12.9603 14.1388 12.9603 13.6112 13.2857 13.2857Z"
                        fill=""
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    onChange={handleSearch}
                    placeholder="Search services..."
                    className="w-full rounded-md border border-stroke bg-transparent py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none"
                  />
                </div>
              </div>
            </div>
          </div>
          {error && !services.length ? (
            <ErrorState onRetry={() => refetch()} />
          ) : (
            <>
              {error && <ErrorBanner onRetry={() => refetch()} />}
              <Table
                bordered
                rowKey="_id"
                onChange={handleTableChange}
                {...(firstLoad
                  ? skeletonTableProps(columns)
                  : {
                      columns,
                      dataSource: services,
                      loading,
                      pagination: {
                        position: ["bottomCenter"],
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: totalCount,
                        showSizeChanger: true,
                      },
                    })}
                locale={{
                  emptyText: searchText ? (
                    <EmptyState
                      title="No services match your search"
                      description={`Nothing found for "${searchText}".`}
                      actionLabel="Clear search"
                      onAction={() => setSearchText("")}
                    />
                  ) : (
                    <EmptyState
                      title="No services available"
                      description="Services offered to your company will appear here."
                    />
                  ),
                }}
              />
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AgentServices;
